<div class="container-box shadow-none">
  <div class="d-flex gap-3 align-items-center p-3">
    <img src="/assets/images/channel-wizards/listing-icon1.webp" alt="" height="40" />
    <div>
      <h6 class="mb-0" i18n>Connecting</h6>
      <div class="text-sm">
        <ng-container i18n>This section will prepare you for connecting Airbnb with AdvanceCM.</ng-container> <br />
        <ng-container i18n>Please read through these instructions carefully before proceeding.</ng-container>
      </div>
    </div>
  </div>
  <div class="border-top p-4 d-flex gap-5">
    <div class="flex-fill">
      <div *ngIf="isProcessing && autoStep" class="alert alert-secondary-info d-flex align-items-center gap-2">
        <i class="far fa-spinner fa-spin"></i>
        <ng-container *ngIf="autoStep === 'import'" i18n>Importing your bookings from Airbnb…</ng-container>
        <ng-container *ngIf="autoStep === 'availability'" i18n>Pushing your availability to Airbnb…</ng-container>
      </div>
      <div *ngIf="autoChainFailed" class="alert alert-secondary-danger d-flex align-items-center gap-3">
        <div class="flex-fill" i18n>
          Automatic setup could not complete. You can retry, or run the remaining steps below manually.
        </div>
        <button class="btn btn-secondary-danger flex-shrink-0" (click)="onRetryAutoChain()" i18n>Retry</button>
      </div>
      <mat-accordion>
        <mat-expansion-panel [expanded]="true" #linkProperty>
          <mat-expansion-panel-header>
            <mat-panel-title>
              <div class="step-number mr-2">1</div>
              <ng-container i18n>Link your property</ng-container>
              <i class="far fa-check-circle text-success ml-2" *ngIf="isLinked"></i>
            </mat-panel-title>
          </mat-expansion-panel-header>
          <div>
            <div *ngIf="isDisconnected" class="alert alert-secondary-danger d-flex align-items-center gap-3">
              <div class="flex-fill">
                <div class="text-semi-bold" i18n>This Airbnb connection is broken</div>
                {{ disconnectedMessage }}
              </div>
              <button *ngIf="canReconnect" class="btn btn-secondary-danger flex-shrink-0" (click)="onReconnect()" i18n>
                Reconnect
              </button>
            </div>

            <p i18n>
              The next step in the process will be to link an existing AdvanceCM rental to your Airbnb room ID.
            </p>

            <p class="text-semi-bold" i18n>Please take note of the following:</p>

            <p i18n>
              A Airbnb property ID may include more than one room ID. These rooms should have their own rates and
              availability calendar and not be listed as inventory available on the same property ID. You can confirm
              this by checking your rates and availability calendar on Airbnb and ensuring that the Rooms to Sell
              section shows either 1 or 0 but never more than 1.
            </p>
            <p i18n>
              If your property shows more than 1 room available within a property ID on the same calendar you will need
              to contact your Airbnb account manager to have them split these rentals into their own rates calendars.
            </p>
            <div *ngIf="isLinked && !isRentalRatesReady" class="alert alert-secondary-warning2">
              <ng-container i18n
                >Some rental rate information is still missing. You can continue — you will review and confirm your
                rates on the Rates step.</ng-container
              >
              <ul class="mb-0 mt-1">
                <li *ngFor="let item of ratesMissing">{{ item }}</li>
              </ul>
            </div>
            <div class="d-flex align-items-center gap-3">
              <mat-form-field2 class="w-50" [showLabel]="false">
                <app-select
                  [formControl]="rentalIdCtrl"
                  [items]="rentals$ | async"
                  bindLabel="name"
                  bindValue="id"
                  [multiple]="false"
                  [required]="true"
                  [searchable]="true"
                  [disabled]="!!connection.rentalId || isDisconnected"
                  placeholder="Select Rental to Link"
                  i18n-placeholder
                >
                </app-select>
              </mat-form-field2>
              <button class="btn btn-light-info" (click)="onRefreshRentals()">
                <i class="far fa-sync"></i> Refresh
              </button>
              <div class="flex-fill"></div>
              <button
                class="btn btn-light-info"
                [disabled]="rentalIdCtrl.valid || !!connection.rentalId || isDisconnected"
                (click)="onImportListing()"
              >
                <i class="cs-icon cs-icon-download"></i> <ng-container i18n>Import Rental</ng-container>
              </button>
              <button
                class="btn btn-light-info"
                [disabled]="rentalIdCtrl.invalid || !!connection.rentalId || isDisconnected"
                (click)="onLink()"
              >
                <i class="cs-icon cs-icon-link"></i> <ng-container i18n>Link</ng-container>
              </button>
              <button class="btn btn-light-info" *ngIf="connection.rentalId" (click)="onRates()">
                <i class="far fa-calculator"></i> <ng-container i18n>Edit Rates</ng-container>
              </button>
              <button class="btn btn-light-info" (click)="reviewErrors.open()" [disabled]="!isLinked">
                <ng-container i18n>Next</ng-container> <i class="cs-icon cs-icon-arrow-down-long"></i>
              </button>
            </div>
          </div>
        </mat-expansion-panel>
        <mat-expansion-panel #reviewErrors [disabled]="!isLinked">
          <mat-expansion-panel-header>
            <mat-panel-title>
              <div class="step-number mr-2">2</div>
              <ng-container i18n>Troubleshooting AirBnb Review Errors</ng-container>
            </mat-panel-title>
          </mat-expansion-panel-header>
          <div>
            <p i18n>
              After setting your synchronization type you may have received an error from AirBnb calling out a
              recommended change to your listing details.
            </p>
            <p i18n>
              To review, the following criteria must be met before AirBnb will allow the listing to connect to a channel
              manager.
            </p>

            <ul>
              <li i18n>A minimum of 7 photos (3 of them being high quality).</li>
              <li i18n>A listing Title that does not exceed 50 characters in length.</li>
              <li i18n>A listing Summary(Description) that is a minimum of 50 characters but no more than 500.</li>
              <li i18n>
                The listing is not a co-host listing. If this is a co-host listing there will be additional steps in the
                process for you to complete later but you can proceed beyond this step.
              </li>
            </ul>
            <p i18n>
              Please ensure that you address the required change mentioned in the error message and then return to this
              page to repeat the step of selecting a synchronization type.
            </p>
            <p i18n>
              If you have received no errors and instead received a success message saying Sync category updated
              successfully please hit Continue to proceed.
            </p>
            <div class="d-flex ali-items-center gap-3">
              <div class="flex-fill"></div>
              <button class="btn btn-light-info" (click)="importBookings.open()">
                <ng-container i18n>Next</ng-container> <i class="cs-icon cs-icon-arrow-down-long"></i>
              </button>
            </div>
          </div>
        </mat-expansion-panel>
        <mat-expansion-panel #importBookings [disabled]="!isLinked">
          <mat-expansion-panel-header>
            <mat-panel-title>
              <div class="step-number mr-2">3</div>
              <ng-container i18n>Import Bookings</ng-container>
              <i class="far fa-check-circle text-success ml-2" *ngIf="isBookingImported"></i>
            </mat-panel-title>
          </mat-expansion-panel-header>
          <div>
            <p i18n>
              Now that AdvanceCM is connected to Airbnb you will need to import your bookings. Please note that
              AdvanceCM can only import current and future bookings. Only limited historical information is provided
              when importing from Airbnb.
            </p>

            <p i18n>
              Future bookings which were created in the past will be imported but bookings in which the departure date
              has already passed will not be.
            </p>

            <p i18n>
              The import starts automatically once your rental is linked. Use the Import Bookings button below if you
              want to run it again.
            </p>
            <div class="d-flex ali-items-center gap-3">
              <button class="btn btn-light-info" [disabled]="isDisconnected" (click)="onImport()">
                <i class="cs-icon cs-icon-download"></i> <ng-container i18n>Import Bookings</ng-container>
              </button>
              <div class="flex-fill"></div>
              <button class="btn btn-light-info" (click)="pushAvailability.open()" [disabled]="!isBookingImported">
                <ng-container i18n>Next</ng-container> <i class="cs-icon cs-icon-arrow-down-long"></i>
              </button>
            </div>
          </div>
        </mat-expansion-panel>
        <mat-expansion-panel #pushAvailability [disabled]="!isLinked || !isBookingImported">
          <mat-expansion-panel-header>
            <mat-panel-title>
              <div class="step-number mr-2">4</div>
              <ng-container i18n>Push Availability</ng-container>
              <i class="far fa-check-circle text-success ml-2" *ngIf="isAvailabilityPushed"></i>
            </mat-panel-title>
          </mat-expansion-panel-header>
          <div>
            <p i18n>
              This step pushes the availability that shows on your AdvanceCM calendar to Airbnb. It runs automatically
              after your rental is linked, and you can re-run it here at any time.
            </p>

            <p i18n>
              An empty AdvanceCM calendar is fine — your dates will simply be pushed as available. Availability pushes
              are processed in the background and normally reach Airbnb within a few minutes.
            </p>
            <div class="d-flex ali-items-center gap-3">
              <button class="btn btn-light-info" [disabled]="isDisconnected" (click)="onPushAvailability()">
                <i class="cs-icon cs-icon-upload"></i> <ng-container i18n>Push Availability</ng-container>
              </button>
              <div class="flex-fill"></div>
            </div>
          </div>
        </mat-expansion-panel>
      </mat-accordion>
    </div>
  </div>
</div>
<div class="mt-3 d-flex align-items-center justify-content-end gap-3">
  <button
    class="btn btn-secondary-info"
    [disabled]="isProcessing || !isLinked || !isBookingImported || !isAvailabilityPushed"
    (click)="next.emit()"
  >
    <ng-container i18n>Next</ng-container>
    <i class="far fa-spin fa-spinner" *ngIf="isProcessing"></i>
    <i class="cs-icon cs-icon-arrow-right-long text-xxs" *ngIf="!isProcessing"></i>
  </button>
</div>
